import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, MessageCircle, Send, Users } from "lucide-react";
import { toast } from "sonner";
import { Layout, PageHeader } from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

function Composer({ user, content, setContent, image, setImage, post }) {
  if (!user) {
    return (
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 text-eon-muted"><Users size={20} className="text-eon-gold" /> Sign in to join the conversation</div>
        <Link to="/login" className="bg-eon-gold text-white text-xs font-bold uppercase tracking-wider px-6 py-2.5 hover:bg-eon-goldhover transition-colors">Sign In</Link>
      </div>
    );
  }
  return (
    <>
      <textarea value={content} onChange={(e) => setContent(e.target.value)} rows={3} placeholder={`What's on your mind, ${user.name?.split(" ")[0]}?`}
        className="w-full bg-eon-bg border border-eon-border px-4 py-3 focus:border-eon-gold outline-none" data-testid="community-post-input" />
      <div className="flex items-center gap-3 mt-3">
        <input value={image} onChange={(e) => setImage(e.target.value)} placeholder="Image URL (optional)"
          className="flex-1 bg-eon-bg border border-eon-border px-4 py-2.5 text-sm focus:border-eon-gold outline-none" />
        <button onClick={post} data-testid="community-post-btn" className="bg-eon-gold text-white text-xs font-bold uppercase tracking-wider px-6 py-2.5 hover:bg-eon-goldhover transition-colors">Post</button>
      </div>
    </>
  );
}

function CommentSection({ detail, comment, setComment, addComment, postId }) {
  return (
    <div className="mt-4 pt-4 border-t border-eon-border space-y-3">
      {detail.comments?.map((c) => (
        <div key={c.id} className="flex gap-3"><div className="w-7 h-7 bg-eon-elevated flex items-center justify-center text-xs font-bold text-eon-gold shrink-0">{c.author_name?.[0]}</div><div className="bg-eon-bg border border-eon-border px-3 py-2 flex-1"><div className="text-xs text-eon-gold font-bold">{c.author_name}</div><div className="text-sm text-eon-text/85">{c.content}</div></div></div>
      ))}
      {detail.comments?.length === 0 && <div className="text-sm text-eon-muted">Be the first to comment.</div>}
      <div className="flex items-center gap-2 pt-2">
        <input value={comment} onChange={(e) => setComment(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addComment()} placeholder="Write a comment..."
          className="flex-1 bg-eon-bg border border-eon-border px-3 py-2 text-sm focus:border-eon-gold outline-none" data-testid={`comment-input-${postId}`} />
        <button onClick={addComment} className="text-eon-gold p-2"><Send size={18} /></button>
      </div>
    </div>
  );
}

function PostItem({ p, user, like, openPost, open, detail, comment, setComment, addComment }) {
  const liked = (p.likes || []).includes(user?.id);
  return (
    <div className="eon-card p-6" data-testid={`community-post-${p.id}`}>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-eon-elevated flex items-center justify-center font-display font-bold text-eon-gold">{p.author_name?.[0] || "?"}</div>
        <div><div className="font-display font-bold">{p.author_name}</div><div className="eon-label">{new Date(p.created_at).toLocaleDateString()}</div></div>
      </div>
      <p className="mt-4 text-eon-text/90 leading-relaxed whitespace-pre-wrap">{p.content}</p>
      {p.image && <img src={p.image} alt="" className="mt-4 w-full max-h-96 object-cover border border-eon-border" />}
      <div className="flex items-center gap-6 mt-4 pt-4 border-t border-eon-border">
        <button onClick={() => like(p.id)} data-testid={`community-like-${p.id}`} className={`flex items-center gap-2 text-sm transition-colors ${liked ? "text-eon-gold" : "text-eon-muted hover:text-eon-gold"}`}>
          <Heart size={16} className={liked ? "fill-eon-gold" : ""} /> {(p.likes || []).length}
        </button>
        <button onClick={() => openPost(p.id)} data-testid={`community-comment-${p.id}`} className="flex items-center gap-2 text-sm text-eon-muted hover:text-eon-gold transition-colors">
          <MessageCircle size={16} /> {p.comment_count || 0}
        </button>
      </div>
      {open === p.id && detail && (
        <CommentSection detail={detail} comment={comment} setComment={setComment} addComment={addComment} postId={p.id} />
      )}
    </div>
  );
}

export default function Community() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [posts, setPosts] = useState([]);
  const [content, setContent] = useState("");
  const [image, setImage] = useState("");
  const [open, setOpen] = useState(null);
  const [comment, setComment] = useState("");
  const [detail, setDetail] = useState(null);

  const load = () => api.get("/community/posts").then((r) => setPosts(r.data));
  useEffect(() => { load(); }, []);

  const post = async () => {
    if (!user) { toast.error("Sign in to post"); nav("/login"); return; }
    if (!content.trim()) return;
    try { await api.post("/community/posts", { content, image }); setContent(""); setImage(""); toast.success("Posted"); load(); }
    catch { toast.error("Could not post"); }
  };

  const like = async (id) => {
    if (!user) { toast.error("Sign in to like"); return; }
    try { const { data } = await api.post(`/community/posts/${id}/like`); setPosts((p) => p.map((x) => x.id === id ? { ...x, likes: data.liked ? [...(x.likes || []), user.id] : (x.likes || []).filter((u) => u !== user.id) } : x)); }
    catch { toast.error("Could not like"); }
  };

  const openPost = async (id) => { setOpen(id); const { data } = await api.get(`/community/posts/${id}`); setDetail(data); };
  const addComment = async () => {
    if (!user) { toast.error("Sign in to comment"); return; }
    if (!comment.trim()) return;
    try { await api.post(`/community/posts/${open}/comments`, { content: comment }); setComment(""); const { data } = await api.get(`/community/posts/${open}`); setDetail(data); load(); }
    catch { toast.error("Could not comment"); }
  };

  return (
    <Layout>
      <PageHeader label="Boerboel Community" title="Owners, Breeders & Enthusiasts"
        subtitle="Share wins, ask questions and connect with the E.O.N Boerboel community. Posts are lightly moderated for a respectful space." />
      <div className="max-w-3xl mx-auto px-5 lg:px-8 py-10">
        <div className="eon-card p-5 mb-8">
          <Composer user={user} content={content} setContent={setContent} image={image} setImage={setImage} post={post} />
        </div>

        <div className="space-y-5">
          {posts.map((p) => (
            <PostItem key={p.id} p={p} user={user} like={like} openPost={openPost}
              open={open} detail={detail} comment={comment} setComment={setComment} addComment={addComment} />
          ))}
        </div>
      </div>
    </Layout>
  );
}
